import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, Loader2, AlertCircle } from 'lucide-react'
import { listGroups, deleteGroup, type YepGroup } from '../../lib/api/users'
import { PageHead } from '../config/ui'
import { GroupDrawer } from './GroupDrawer'

export function GroupsPage() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['groups'], queryFn: listGroups })
  const [target, setTarget] = useState<YepGroup | 'new' | null>(null)

  const del = useMutation({
    mutationFn: (id: string) => deleteGroup(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  })

  const rows = q.data ?? []

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      <PageHead
        title="Gruppi e permessi"
        description="Gruppi di operatori e relativi permessi (lettura / creazione / modifica / eliminazione) per ogni sezione del gestionale."
      >
        <button
          onClick={() => setTarget('new')}
          className="inline-flex items-center gap-2 rounded-lg bg-verdigris px-4 py-[10px] text-[13.5px] font-semibold text-white hover:bg-verdigris-700"
        >
          <Plus className="h-4 w-4" /> Nuovo gruppo
        </button>
      </PageHead>

      <div className="cv-panel overflow-hidden">
        {q.isError ? (
          <div className="px-4 py-16 text-center text-sm text-muted">
            <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare i gruppi.
          </div>
        ) : q.isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-faint" />
          </div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-16 text-center text-sm text-muted">Nessun gruppo.</div>
        ) : (
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="border-b border-line-soft bg-paper/60 text-[11px] font-bold uppercase tracking-[1px] text-muted">
                <th className="px-5 py-3 text-left">Gruppo</th>
                <th className="px-5 py-3 text-left">Operatori</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.id ?? g.name} className="border-b border-line-soft last:border-0 hover:bg-paper/40">
                  <td className="px-5 py-3 font-semibold text-coal">{g.name}</td>
                  <td className="px-5 py-3 text-slate">{g.userIds?.length ?? 0} operatori</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setTarget(g)} className="rounded-md p-1.5 text-slate hover:bg-paper hover:text-coal" title="Modifica">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (g.id && window.confirm(`Eliminare il gruppo "${g.name}"?`)) del.mutate(g.id)
                        }}
                        className="rounded-md p-1.5 text-slate hover:bg-venetian-soft hover:text-venetian"
                        title="Elimina"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <GroupDrawer target={target} onClose={() => setTarget(null)} />
    </div>
  )
}
